import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, Alert, Container, Chip, CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, InsertPhoto as PhotoIcon } from '@mui/icons-material';
import { blogService, type BlogAccess } from '../services/blogService';
import { locationsAPI, type EventType, type City } from '../services/api';
import { useAuthStore } from '../store/authStore';
import BlogEditor from '../components/BlogEditor';
import { PollCreator } from '../components/BlogPoll';

const BlogCreatePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const isEdit = !!id;

  const cameFromAdmin = location.state?.from === 'admin' || document.referrer.includes('/admin');

  const [access, setAccess] = useState<BlogAccess | null>(null);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [savedArticleId, setSavedArticleId] = useState<number | null>(null);
  const [loadingArticle, setLoadingArticle] = useState(isEdit);

  const [form, setForm] = useState({
    title: '', excerpt: '', content: '',
    event_type_id: null as number | null, city_id: null as number | null,
  });

  useEffect(() => {
    locationsAPI.getEventTypes().then(setEventTypes).catch(() => {});
    locationsAPI.getAllCities().then(setCities).catch(() => {});
    blogService.checkAccess().then(setAccess).catch(() => setAccess({ allowed: false, reason: 'Ошибка проверки доступа' }));

    if (isEdit) {
      blogService.getMyArticle(Number(id)).then(a => {
        setForm({
          title: a.title, excerpt: a.excerpt || '', content: a.content,
          event_type_id: a.event_type_id, city_id: a.city_id,
        });
        setCoverImage(a.cover_image || null);
        setSavedArticleId(a.id);
      }).catch(() => navigate('/blog')).finally(() => setLoadingArticle(false));
    }
  }, [id]);

  const handleBack = () => {
    if (cameFromAdmin) {
      navigate('/admin', { state: { tab: 'blog', scrollToArticle: id ? Number(id) : undefined } });
    } else {
      navigate(-1);
    }
  };

  const handleCoverUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (file.size > 800 * 1024) { alert('Размер файла не более 800 КБ'); return; }
      try {
        setUploadingCover(true);
        const result = await blogService.uploadImage(file);
        setCoverImage(result.url);
      } catch { alert('Ошибка загрузки'); }
      finally { setUploadingCover(false); }
    };
    input.click();
  };

  const handleSave = async (asDraft: boolean) => {
    if (saving) return;
    if (!form.title.trim() || form.title.trim().length < 3) { setError('Заголовок не менее 3 символов'); return; }
    const plainContent = form.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!plainContent && !/<img/i.test(form.content)) { setError('Содержимое не может быть пустым'); return; }
    if (!form.event_type_id) { setError('Выберите вид мероприятия'); return; }

    try {
      setSaving(true);
      const data = {
        title: form.title, excerpt: form.excerpt, content: form.content,
        cover_image: coverImage, event_type_id: form.event_type_id, city_id: form.city_id,
        status: asDraft ? 'draft' : 'published',
      };
      let articleId = id ? Number(id) : undefined;
      if (isEdit) {
        await blogService.updateArticle(Number(id), data);
        articleId = Number(id);
      } else {
        const created = await blogService.createArticle(data);
        articleId = created.id;
      }
      setSavedArticleId(articleId);

      if (asDraft) {
        navigate('/admin', { state: { tab: 'blog', scrollToArticle: articleId } });
      } else {
        navigate(`/blog?author_id=${user?.id}`);
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Ошибка сохранения');
      setSaving(false);
    }
  };

  if (access && !access.allowed) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>{access.reason}</Alert>
          <Button onClick={() => navigate('/blog')}>К блогу</Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} size="small" sx={{ mb: 2 }}>
          {cameFromAdmin ? 'В админ-панель' : 'Назад'}
        </Button>
        <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, mb: 2 }}>
          {isEdit ? 'Редактирование статьи' : 'Новая статья'}
        </Typography>

        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          {loadingArticle ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">Загрузка статьи...</Typography>
            </Box>
          ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth label="Заголовок" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required size="small"
              inputProps={{ maxLength: 200 }} helperText={`${form.title.length}/200`} />

            <TextField fullWidth label="Краткое описание" value={form.excerpt}
              onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} size="small"
              multiline rows={2} helperText="Отображается в списке статей" inputProps={{ maxLength: 300 }} />

            {/* Rich text editor */}
            <Box>
              <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>Содержимое статьи</Typography>
              <BlogEditor
                value={form.content}
                onChange={content => setForm(f => ({ ...f, content }))}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Autocomplete options={eventTypes} getOptionLabel={o => `${o.icon_emoji || ''} ${o.name}`}
                renderInput={p => <TextField {...p} label="Вид мероприятия *" size="small" />}
                value={eventTypes.find(t => t.id === form.event_type_id) || null}
                onChange={(_, v) => setForm(f => ({ ...f, event_type_id: v?.id || null }))}
                isOptionEqualToValue={(o, v) => o.id === v?.id} sx={{ flex: 1, minWidth: 200 }} size="small" />
              <Autocomplete options={cities} getOptionLabel={o => o.name}
                renderInput={p => <TextField {...p} label="Город (если релевантно)" size="small" />}
                value={cities.find(c => c.id === form.city_id) || null}
                onChange={(_, v) => setForm(f => ({ ...f, city_id: v?.id || null }))}
                isOptionEqualToValue={(o, v) => o.id === v?.id} sx={{ flex: 1, minWidth: 200 }} size="small" />
            </Box>

            {/* Cover image */}
            <Box>
              <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>Обложка статьи</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small" startIcon={<PhotoIcon />}
                  onClick={handleCoverUpload} disabled={uploadingCover}
                  sx={{ fontSize: '0.8rem' }}>
                  {uploadingCover ? 'Загрузка...' : 'Загрузить обложку'}
                </Button>
                {coverImage && (
                  <Chip label="Обложка загружена ✓" color="success" size="small"
                    onDelete={() => setCoverImage(null)} />
                )}
              </Box>
              {coverImage && (
                <Box sx={{ mt: 1, width: 120, height: 80, borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  <img src={coverImage} alt="Обложка" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              )}
            </Box>

            {/* Poll creator (only for existing articles) */}
            {savedArticleId && (
              <Box>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>Опрос в тексте статьи</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Опрос будет встроен в статью. Позиция определяется маркером [poll:ID] в тексте.
                </Typography>
                <PollCreator articleId={savedArticleId} onCreated={(pollId: number) => {
                  const marker = `<p>[poll:${pollId}]</p>`;
                  setForm(f => ({ ...f, content: f.content.replace(/<p><br><\/p>$/i, '') + marker }));
                }} />
              </Box>
            )}

            {/* Warnings near buttons */}
            {!form.event_type_id && (
              <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>Выберите вид мероприятия — это обязательное поле.</Alert>
            )}
            {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }} onClose={() => setError('')}>{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={handleBack}>
                {cameFromAdmin ? 'В админ-панель' : 'Отмена'}
              </Button>
              <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => handleSave(true)} disabled={saving}
                sx={{ borderColor: '#1565c0', color: '#1565c0', '&:hover': { borderColor: '#0d47a1', bgcolor: 'rgba(21,101,192,0.04)' } }}>
                {saving ? 'Сохранение...' : 'Сохранить черновик'}
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleSave(false)} disabled={saving}
                sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}>
                {saving ? 'Публикация...' : 'Опубликовать статью'}
              </Button>
            </Box>
            <Alert severity="info" sx={{ fontSize: '0.78rem' }}>Черновик виден только вам. Опубликованная статья будет доступна всем пользователям.</Alert>
          </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default BlogCreatePage;